import { useMemo } from 'react';
import { useFormState } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { useGetTerminalMaterialIdsQuery } from '@/lib/api/lookupsApi';
import { FACILITY_KINDS, buildMaterialOptions, parseFacilityValue } from '@/config/formOptions';
import { DependentDropdownField } from './DependentDropdownField';

/**
 * Props:
 *   materials        the account's materials; only certified ones are offered
 *   certificateType  when given, also scopes to the active module's materials
 *   clears           downstream field names to reset when the material changes
 *   disabled         locked once something downstream depends on it
 */
export const MaterialField = ({ materials = [], certificateType, clears, disabled = false }) => {
  const t = useTranslations();
  const { values } = useFormState();

  const facility = useMemo(() => parseFacilityValue(values.facilityId), [values.facilityId]);
  const siteId = facility.kind === FACILITY_KINDS.site ? facility.id : null;
  const terminalId = siteId ? values.terminalId || null : null;

  // A picked terminal narrows the site's materials to the ones Material settings assigned to it.
  const { data: terminalMaterialIds = null, isFetching: isTerminalLoading } =
    useGetTerminalMaterialIdsQuery({ siteId, terminalId }, { skip: !terminalId });

  const options = useMemo(
    () =>
      buildMaterialOptions({
        materials,
        siteId,
        certificateType,
        terminalMaterialIds: terminalId ? terminalMaterialIds : null,
      }),
    [materials, siteId, certificateType, terminalId, terminalMaterialIds],
  );

  return (
    <DependentDropdownField
      name="materialId"
      label={t('FIELD_MATERIAL_NAME')}
      placeholder={t('FORM_SELECT_MATERIAL')}
      emptyMessage={values.facilityId ? t('FORM_MATERIAL_EMPTY') : t('FORM_SELECT_FACILITY_FIRST')}
      options={options}
      clears={clears}
      disabled={disabled}
      ready={!isTerminalLoading}
    />
  );
};
